import React, { FC } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { FieldGroup } from '@xsolla/ui-core';
import { ImageUploader, UploaderValue } from '@xsolla/ui-uploader';

import { useConfig } from '../../../../contexts/config';
import { IMAGE_MAX_FILE_SIZE_KB, IMAGE_RECOMMEND_SIZE_PX } from './constants';
import { createLogoUploader } from './logoUploader';
import { useImageUploaderValue } from './useImageUploaderValue';
import { formatUrl } from './utils';

interface FormValues {
  logoUrl: string;
}

export const LogoFormField: FC = () => {
  const intl = useIntl();
  const { merchantId } = useConfig();

  const {
    values: { logoUrl },
    errors,
    submitFailed,
    modified
  } = useFormState<FormValues>();

  const image = useImageUploaderValue(logoUrl);

  const uploadLogo = createLogoUploader(merchantId, {
    sizeError: intl.formatMessage(
      { id: 'authorization.settings.babkaLogin.field.logo-url.size-error' },
      { size: IMAGE_MAX_FILE_SIZE_KB }
    ),
    uploadError: intl.formatMessage({
      id: 'authorization.settings.babkaLogin.field.logo-url.upload-error'
    })
  });

  return (
    <FieldGroup
      label={intl.formatMessage({ id: 'authorization.settings.babkaLogin.field.logo-url.title' })}
      tooltip={intl.formatMessage(
        { id: 'authorization.settings.babkaLogin.field.logo-url.tooltip' },
        { br: <br />, sizePx: IMAGE_RECOMMEND_SIZE_PX, sizeKb: IMAGE_MAX_FILE_SIZE_KB }
      )}
      size="md">
      <Field name="logoUrl">
        {({ input }) => (
          <ImageUploader
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type="button" // Prevent automatic form submission
            errorMessage={
              (submitFailed || modified?.logoUrl) &&
              errors?.logoUrl &&
              intl.formatMessage({ id: errors.logoUrl })
            }
            onChange={(file?: UploaderValue): void => {
              if (file && file.url) {
                input.onChange(formatUrl(file.url));
              } else {
                input.onChange('');
              }
              input.onBlur();
            }}
            options={{
              uploadFn: uploadLogo,
              deleteFn: (): Promise<void> => {
                input.onChange('');
                input.onBlur();
                return Promise.resolve();
              }
            }}
            size="md"
            value={image}
          />
        )}
      </Field>
    </FieldGroup>
  );
};
